import React, { useState, useEffect } from 'react'
import Button from 'react-bootstrap/Button'
import FormControl from 'react-bootstrap/FormControl'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import { PhoneInput } from 'react-international-phone'

import SearchBar from '../../../components/SearchBar/SearchBar'
import { CustomTabs } from '../../../styles/commonStyles'
import { useAppSelector } from '../../../hooks'
import { useCurrentUser } from '../../../common'
import { useValidatePhone } from '../../../components/SigninSignup/components/SigninSignupFunctions'
import { SEARCH_GUESTS_IN_BOOKING } from '../../../graphql'
import type { GuestShort, GuestPublic } from '../../../types'

export interface IAddGuestPayload {
  firstName: string
  lastName: string
  email: string
  phone: string
  isPlusOne: boolean
  bookingAddOnId: string
  guest: GuestShort | null
  isExternalGuest: boolean
  isFromSearch?: boolean
}

interface SelectGuestModalProps {
  show: boolean
  onHide: () => void
  onAddGuest: (guest: IAddGuestPayload) => void
  myGroup: GuestShort[]
  showPlusOne: boolean
  currentGuest: GuestShort
}

const SelectGuestModal: React.FC<SelectGuestModalProps> = ({
  show,
  onHide,
  onAddGuest,
  myGroup = [],
  showPlusOne,
  currentGuest,
}) => {
  // Get user and booking info for SearchBar
  const { userId } = useCurrentUser()
  const {
    currentBookingId,
    freeGuests = [],
    paidGuests = [],
  } = useAppSelector(state => ({
    ...state.booking,
    currentBookingId: state.booking.currentBookingId,
  }))

  const [activeTab, setActiveTab] = useState<'group' | 'new'>('group')
  const [newGuest, setNewGuest] = useState<{
    firstName: string
    lastName: string
    email: string
    phone: string | undefined
    isPlusOne: boolean
  }>({
    firstName: '',
    lastName: '',
    email: '',
    phone: undefined,
    isPlusOne: false,
  })
  const [phoneError, setPhoneError] = useState<string>('')
  const { validatePhone } = useValidatePhone()
  const plusOneCount = freeGuests.filter(
    el => el.guest.isPlusOne === true
  ).length

  const availablePlusOne =
    currentGuest.plusOne - currentGuest.plusOneCount > plusOneCount

  // Handle search result selection
  const handleSearchResultSelect = (result: {
    value: string
    displayText: string
    data: Record<string, unknown>
  }) => {
    const eventGuest = result.data as GuestPublic
    onAddGuest({
      firstName: eventGuest.firstName,
      lastName: eventGuest.lastName,
      email: '',
      phone: '',
      isPlusOne: false,
      bookingAddOnId: '',
      guest: {
        id: eventGuest.id,
        firstName: eventGuest.firstName,
        lastName: eventGuest.lastName,
      } as GuestShort,
      isExternalGuest: true,
      isFromSearch: true,
    })
    onHide()
  }

  useEffect(() => {
    if (show) {
      setActiveTab('group')
    }
  }, [show])

  const isFormValid = () => {
    const { firstName, lastName } = newGuest
    if (!firstName || !lastName) return false
    if (newGuest.phone && phoneError) return false
    return true
  }

  const availableGroupMembers = myGroup
    .filter(member => !freeGuests.some(existing => existing.id === member.id))
    .sort((a, b) => {
      if (a.email === currentGuest.email) return -1
      if (b.email === currentGuest.email) return 1
      const nameA = `${a.firstName} ${a.lastName}`
      const nameB = `${b.firstName} ${b.lastName}`
      return nameA.localeCompare(nameB)
    })

  // Create array of already added guest IDs to exclude from search results
  const excludedGuestIds = [
    ...freeGuests.map(guest => guest.id).filter(Boolean),
    ...paidGuests.map(guest => guest.guest.id).filter(Boolean),
  ] as string[]

  const handleGroupSelection = (member: GuestShort) => {
    onAddGuest({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email || '',
      phone: member.phone || '',
      isPlusOne: false,
      bookingAddOnId: '',
      guest: member,
      isExternalGuest: false,
      isFromSearch: false, // Guests from group are never from search
    })
    onHide()
  }

  const handleNewGuestSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isFormValid()) {
      onAddGuest({
        ...newGuest,
        bookingAddOnId: '',
        guest: null,
        phone: newGuest.phone || '',
        isExternalGuest: true,
        isFromSearch: false,
      })
      setNewGuest({
        firstName: '',
        lastName: '',
        email: '',
        phone: undefined,
        isPlusOne: false,
      })
      onHide()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    const isPlusOne = availablePlusOne && checked
    setNewGuest(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? isPlusOne : value,
    }))
  }

  const handlePhoneChange = (value: string) => {
    if (value?.trim() === '+1') return
    const phoneValue = value.trim() === '' ? undefined : value
    setNewGuest(prev => ({
      ...prev,
      phone: phoneValue,
    }))
    validatePhone({
      phone: value,
      setPhoneError,
      required: false,
    })
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Body className="px-3 py-1">
        <CustomTabs>
          <button
            type="button"
            className={activeTab === 'group' ? 'active' : ''}
            onClick={() => setActiveTab('group')}
          >
            My Group
          </button>
          <button
            type="button"
            className={activeTab === 'new' ? 'active' : ''}
            onClick={() => setActiveTab('new')}
          >
            Other Guest
          </button>
        </CustomTabs>

        <div className="pt-3">
          {activeTab === 'group' && (
            <div className="pb-3">
              <h5 className="mb-3 fz-11 tc-lgray">
                Select a guest from your group
              </h5>

              {availableGroupMembers.length === 0 ? (
                <p className="text-center py-3 fz-14">
                  No group members available to add or all members already added
                  to the reservation.
                </p>
              ) : (
                <Table borderless hover size="sm" className="mb-1" responsive>
                  <thead className="border-bottom">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>{/* Empty header for spacing */}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableGroupMembers.map(member => (
                      <tr key={member.id}>
                        <td className="align-middle">
                          {member.firstName} {member.lastName}
                          {member.email === currentGuest.email && (
                            <Badge className="ml-2" variant="primary" pill>
                              you
                            </Badge>
                          )}
                        </td>
                        <td className="align-middle">{member.email}</td>
                        <td className="text-right align-middle">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() => handleGroupSelection(member)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}

          {activeTab === 'new' && (
            <div className="py-3">
              <h5 className="mb-3 fz-14 tc-lgray">
                Search for existing guests from this event
              </h5>

              <div className="mb-4">
                <SearchBar
                  query={SEARCH_GUESTS_IN_BOOKING}
                  dataPath="booking.eventGuests"
                  variables={{
                    bookingId: currentBookingId || '',
                    userId: userId || '',
                  }}
                  valueField="id"
                  displayFields={['firstName', 'lastName']}
                  excludeValues={excludedGuestIds}
                  placeholder="Search guests..."
                  onResultClick={handleSearchResultSelect}
                />
              </div>

              <div className="mb-4">
                <hr />
                <h5 className="fz-14 tc-lgray">Or create a new guest</h5>
              </div>

              <form onSubmit={handleNewGuestSubmit}>
                <div className="form-group">
                  <label htmlFor="firstName">
                    First Name <span className="text-danger">*</span>
                  </label>
                  <FormControl
                    id="firstName"
                    name="firstName"
                    value={newGuest.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <FormControl
                    id="lastName"
                    name="lastName"
                    value={newGuest.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <FormControl
                    id="email"
                    name="email"
                    type="email"
                    value={newGuest.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone-input">Phone</label>
                  <PhoneInput
                    placeholder="Phone number"
                    defaultCountry="us"
                    value={newGuest.phone}
                    onChange={handlePhoneChange}
                    className="international-phone-small"
                    inputClassName="international-phone-input-small"
                    countrySelectorStyleProps={{
                      buttonClassName: 'international-phone-button-small',
                    }}
                  />
                  {phoneError && (
                    <div className="text-danger">{phoneError}</div>
                  )}
                </div>
                {showPlusOne && availablePlusOne && (
                  <div className="form-group form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isPlusOne"
                      name="isPlusOne"
                      checked={newGuest.isPlusOne}
                      onChange={handleInputChange}
                    />
                    <label
                      className="fwsb form-check-label"
                      htmlFor="isPlusOne"
                    >
                      Is this guest your +1?
                    </label>
                  </div>
                )}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="outline-secondary"
                    className="rounded-pill mr-2"
                    onClick={onHide}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="d-block"
                    type="submit"
                    disabled={!isFormValid()}
                  >
                    Add Guest
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default SelectGuestModal
